import { List, Set, Map } from 'immutable'
import { createSelector } from 'reselect'
import {
  compose,
  cond,
  always,
  T,
  equals,
  propEq,
  propSatisfies,
  either,
  __,
} from 'ramda'
import {
  safeToJS,
  getOr,
  getIn,
  getInOr,
  pickToArray,
  pick,
  pluck,
  get,
  filterNot,
  getEq,
} from 'utils/immutable'

import { formatIdAttributeImm } from 'utils/harnesses'
import {
  FEATURE_MODULE_NAMES_WITH_HARNESSES,
  ANALOG_INPUT,
  DIGITAL_INPUT,
  AMPS2,
  AMPS5,
  AMPS20,
  X1,
  X2,
  X3,
  X7,
} from 'utils/constants'
import { INTERNAL } from 'constants/featureTypes'

import { getFeatureType } from 'selectors/feature/featureMetadata'

import { createDeepEqualSelector } from 'selectors/utils'
import { getFeatureModuleName, featureMetaDataSelector } from './shared'

const modulesSelector = getIn(['feature', 'harnesses', 'modules'])

const harnessSelector = (state, props) =>
  state.getIn(['feature', 'harnesses', 'harnesses', props.id])

const harnessesSelector = getIn(['feature', 'harnesses', 'harnesses'])

const harnessPinsSelector = (state, props) =>
  getInOr(
    List(),
    ['feature', 'harnesses', 'harnesses', props.id, 'pins'],
    state
  )

export const getHarness = createSelector(harnessSelector, safeToJS)
export const makeGetHarness = () => getHarness

export const makeGetHarnessPins = () =>
  createSelector(harnessPinsSelector, safeToJS)

export const featureHarnessIdsSelector = createSelector(
  featureMetaDataSelector,
  getOr(List(), 'harnesses')
)

export const getFeatureHarnessIds = createSelector(
  featureHarnessIdsSelector,
  safeToJS
)

const moduleHarnessIdsSelector = createSelector(
  getFeatureModuleName,
  modulesSelector,
  (moduleName, modules) => modules.getIn([moduleName, 'harnesses'])
)

const filteredHarnessesByModuleSelector = createSelector(
  moduleHarnessIdsSelector,
  harnessesSelector,
  featureHarnessIdsSelector,
  (moduleHarnessIds, harnesses, featureHarnesses) => {
    if (!moduleHarnessIds) return List()

    const filteredHarnessIds = moduleHarnessIds.filterNot((id) =>
      featureHarnesses.find(
        (featureHarness) => formatIdAttributeImm(featureHarness) === id
      )
    )
    // remove any duplicates and sort by order
    return Set(pickToArray(filteredHarnessIds, harnesses)).toList().sort()
  }
)

export const makeGetHarnessesByModule = () =>
  createSelector(filteredHarnessesByModuleSelector, safeToJS)

export const getHarnessesLoaded = createSelector(
  getIn(['feature', 'harnesses', 'harnesses']),
  (harnesses) => !harnesses.isEmpty()
)

export const harnessPinIdSetSelector = createSelector(
  featureHarnessIdsSelector,
  harnessesSelector,
  (featureHarnesses, allHarnesses) => {
    const harnessIds = featureHarnesses.map(formatIdAttributeImm)
    const harnesses = pick(harnessIds, allHarnesses)

    return harnesses
      .reduce((acc, current) => {
        const pins = current.get('pins')

        const pinIds = pins.reduce((pinIdAcc, pin) => {
          const pinUri = pin.get('uri')
          // if there is no uri, then it is a pin that doesn't reside in our system (so ignore)
          if (!pinUri) return pinIdAcc

          const isOutputPin = pinUri.includes('/pin')

          // TODO: only use 'uri' when we have time to refactor outputs to use uris
          return pinIdAcc.add(isOutputPin ? pin.get('id') : pinUri)
        }, Set())

        return acc.add(pinIds)
      }, Set())
      .flatten()
  }
)

export const harnessFloatingCavityPinsSelector = createSelector(
  featureHarnessIdsSelector,
  harnessesSelector,
  (featureHarnesses, allHarnesses) => {
    const harnessIds = featureHarnesses.map(formatIdAttributeImm)
    const harnesses = pick(harnessIds, allHarnesses)

    return harnesses
      .reduce((acc, current) => {
        const pins = current.get('pins')
        const pinsFloatCavityType = pins.reduce((pinAcc, pin) => {
          const harnessCavityType = pin.get('cavityType')
          const pinUri = pin.get('uri')
          // if there is no uri, then it is a pin that doesn't reside in our system (so ignore)
          if (harnessCavityType !== 'Float' || !pinUri) return pinAcc
          const isOutputPin = pinUri.includes('/pin')
          // TODO: only use 'uri' when we have time to refactor outputs to use uris
          return pinAcc.add(isOutputPin ? pin.get('id') : pinUri)
        }, Set())
        return acc.add(pinsFloatCavityType)
      }, Set())
      .flatten()
  }
)

// get the pins for harnesses that are on a feature
const featureHarnessPinsSelector = createSelector(
  featureHarnessIdsSelector,
  harnessesSelector,
  (featureHarnesses, allHarnesses) => {
    const harnessIds = featureHarnesses.map(formatIdAttributeImm)
    const harnesses = pickToArray(harnessIds, allHarnesses)

    return harnesses.reduce((harnessesAcc, harness) => {
      const harnessPins = harness.get('pins').reduce((acc, pin) => {
        const pinUri = pin.get('uri')
        // if there is no uri, then it is a pin that doesn't reside in our system (so ignore)
        if (!pinUri) return acc

        // need to know if this is an output pin as they are normalized based on different properties
        // TODO: align so they are based of either 'uri' or 'id'
        const isOutputPin = pinUri.includes('/pin')

        const idAttr = isOutputPin ? 'id' : 'uri'

        return acc.set(pin.get(idAttr), pin)
      }, Map())

      return harnessesAcc.merge(harnessPins)
    }, Map())
  }
)

// get the floating circuits for harnesses that are on a feature
const filterAndPluckCircuits = (subType, outputCurrent) =>
  compose(pluck('circuit'), filterPins(subType, outputCurrent), get('pins'))

// helpers for pinFilterMapping
const connectorEq = propEq('connector')
const outputCurrentSatisfies = propSatisfies(__, 'outputCurrent')
const subTypeSatisfies = propSatisfies(__, 'subType')

const pinFilterMapping = cond([
  [connectorEq(X1), subTypeSatisfies(equals(ANALOG_INPUT))],
  [
    connectorEq(X2),
    either(
      subTypeSatisfies(equals(DIGITAL_INPUT)),
      outputCurrentSatisfies(equals(AMPS2))
    ),
  ],
  [
    connectorEq(X3),
    outputCurrentSatisfies(either(equals(AMPS5), equals(AMPS20))),
  ],
  [
    connectorEq(X7),
    either(
      subTypeSatisfies(either(equals(DIGITAL_INPUT), equals(ANALOG_INPUT))),
      outputCurrentSatisfies(equals(AMPS5))
    ),
  ],
  [T, always(false)],
])

// for each pin do the following:
// get the connector (X1, X2, X3, X7)
// if X1: Analog Input
// if X2: Digital Input / 2A output
// if X3: 5A output / 20A output
// if X7: Digital Input / Analog Input / 5A output
const filterPins = (subType, outputCurrent) => (pins) => {
  return pins.filter((pin) => {
    const ignoreMapping = pin.get('ignoreMapping')
    if (ignoreMapping) return false

    const connector = pin.get('connector')
    return pinFilterMapping({ connector, subType, outputCurrent })
  })
}

const floatingPinsReducer = (subType, outputCurrent) => (acc, current) => {
  const cavityType = current.get('cavityType')

  if (cavityType !== 'Float') return acc

  return acc.concat(filterAndPluckCircuits(subType, outputCurrent)(current))
}

export const featureHarnessFloatingCircuitsSelector = createSelector(
  featureHarnessIdsSelector,
  harnessesSelector,
  (_, props) => props,
  (featureHarnesses, allHarnesses, { subType, outputCurrent }) => {
    const harnessIds = featureHarnesses.map(formatIdAttributeImm)

    const harnesses = pickToArray(harnessIds, allHarnesses)

    return harnesses.reduce(floatingPinsReducer(subType, outputCurrent), Set())
  }
)

export const getFeatureHarnessFloatingCircuits = createDeepEqualSelector(
  featureHarnessFloatingCircuitsSelector,
  safeToJS
)

const pluckCircuits = compose(
  pluck('circuit'),
  filterNot(getEq('ignoreMapping', true)),
  get('pins')
)

const allFloatingPinsReducer = (acc, current) => {
  const cavityType = current.get('cavityType')

  if (cavityType !== 'Float') return acc

  return acc.concat(pluckCircuits(current))
}

export const allFeatureHarnessFloatingCircuitsSelector = createSelector(
  featureHarnessIdsSelector,
  harnessesSelector,
  (featureHarnesses, allHarnesses) => {
    const harnessIds = featureHarnesses.map(formatIdAttributeImm)
    const harnesses = pickToArray(harnessIds, allHarnesses)

    return harnesses.reduce(allFloatingPinsReducer, Set())
  }
)

export const getAllFeatureHarnessFloatingCircuits = createDeepEqualSelector(
  allFeatureHarnessFloatingCircuitsSelector,
  safeToJS
)

export const getPinCircuitSelector = createSelector(
  featureHarnessPinsSelector,
  (_, { id }) => id,
  (pins, id) => pins.getIn([id, 'circuit'])
)

export const makeGetPinCircuit = () => getPinCircuitSelector

export const getShowHarnesses = createSelector(
  getFeatureModuleName,
  getFeatureType,
  (featureModuleName, featureType) => {
    return (
      featureType === INTERNAL &&
      FEATURE_MODULE_NAMES_WITH_HARNESSES.includes(featureModuleName)
    )
  }
)
